import { addDays } from 'date-fns';
import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { getErrorMessage } from '@/lib/error';
import { parseDateOnly } from '@/lib/dateOnly';
import {
  createCalendarEvent,
  deleteCalendarEvent,
  fetchCalendarBootstrap,
  fetchCalendarEvents,
  fetchCalendars,
  searchCalendarEvents,
  updateCalendarColor as updateCalendarColorApi,
  updateCalendarEvent,
} from '@/api/calendarApi';
import type {
  CalendarColorUpdateInput,
  CalendarEventCreateInput,
  CalendarEventDeleteInput,
  CalendarEventModel,
  CalendarEventUpdateInput,
  CalendarRange,
  CalendarSearchOptions,
} from '@/types/calendar';
import { useCalendarStore } from './calendarStore';

function computeDefaultRange() {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 1);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  end.setDate(end.getDate() + 30);
  return {
    timeMin: start.toISOString(),
    timeMax: end.toISOString(),
  };
}

function mergeRanges(current: CalendarRange | null, incoming: CalendarRange): CalendarRange {
  if (!current) {
    return incoming;
  }
  const currentMin = Date.parse(current.timeMin);
  const currentMax = Date.parse(current.timeMax);
  const incomingMin = Date.parse(incoming.timeMin);
  const incomingMax = Date.parse(incoming.timeMax);
  return {
    timeMin: !Number.isFinite(currentMin) || (Number.isFinite(incomingMin) && incomingMin < currentMin) ? incoming.timeMin : current.timeMin,
    timeMax: !Number.isFinite(currentMax) || (Number.isFinite(incomingMax) && incomingMax > currentMax) ? incoming.timeMax : current.timeMax,
  };
}

function deriveEventRange(event: CalendarEventModel): CalendarRange | null {
  const startValue = event.start?.dateTime ?? (event.start?.date ? `${event.start.date}T00:00:00Z` : null);
  const endValue = event.end?.dateTime ?? (event.end?.date ? `${event.end.date}T23:59:59Z` : null);
  if (!startValue || !endValue) {
    return null;
  }
  const startDate = new Date(startValue);
  const endDate = new Date(endValue);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }
  return {
    timeMin: startDate.toISOString(),
    timeMax: endDate.toISOString(),
  };
}

function buildRangeKey(calendarUids: string[], timeMin: string, timeMax: string, extra = '') {
  const unique = Array.from(new Set(calendarUids)).sort();
  return `${unique.join(',')}|${timeMin}|${timeMax}|${extra}`;
}

function resolveCalendarsForFetch(storeState: ReturnType<typeof useCalendarStore.getState>) {
  const calendarSelection = storeState.useDefaultVisibleCalendars
    ? storeState.defaultCalendarUids
    : storeState.visibleCalendarUids;
  const taskSelection = storeState.taskUseDefaultVisibleCalendars
    ? storeState.defaultCalendarUids
    : storeState.taskVisibleCalendarUids;
  const combined = new Set([...calendarSelection, ...taskSelection]);
  return Array.from(combined);
}

interface CalendarActionsState {
  bootstrapLoading: boolean;
  bootstrapError: string | null;
  refreshingCalendars: boolean;
  rangeLoadingKey: string | null;
  creatingEvent: boolean;
  updatingEventId: string | null;
  deletingEventId: string | null;
  mutationError: string | null;
  searchLoading: boolean;
  searchError: string | null;
  searchResults: string[];
  searchRange: CalendarRange | null;
  lastSearchContext: {
    query: string | null;
    calendarUids: string[];
  } | null;
  bootstrap: (options?: { force?: boolean; timeMin?: string; timeMax?: string }) => Promise<void>;
  fetchEventsForRange: (options?: {
    calendarUids?: string[];
    timeMin?: string;
    timeMax?: string;
    replace?: boolean;
    showDeleted?: boolean;
    query?: string | null;
  }) => Promise<void>;
  refreshCalendars: (options?: { accountId?: string | null }) => Promise<void>;
  prefetchRangeAround: (focusDate: string, options?: { daysBefore?: number; daysAfter?: number }) => Promise<void>;
  createEvent: (input: CalendarEventCreateInput) => Promise<CalendarEventModel | null>;
  updateEvent: (input: CalendarEventUpdateInput) => Promise<CalendarEventModel | null>;
  deleteEvent: (input: CalendarEventDeleteInput) => Promise<boolean>;
  updateCalendarColor: (input: CalendarColorUpdateInput) => Promise<void>;
  searchEvents: (options: CalendarSearchOptions) => Promise<CalendarEventModel[]>;
  clearSearchResults: () => void;
}

export const useCalendarActionsStore = create<CalendarActionsState>()(
  conditionalDevtools((set, get) => ({
    bootstrapLoading: false,
    bootstrapError: null,
    refreshingCalendars: false,
    rangeLoadingKey: null,
    creatingEvent: false,
    updatingEventId: null,
    deletingEventId: null,
    mutationError: null,
    searchLoading: false,
    searchError: null,
    searchResults: [],
    searchRange: null,
    lastSearchContext: null,
    bootstrap: async (options) => {
      const store = useCalendarStore.getState();
      if (store.initialized && !options?.force) {
        return;
      }
      set({ bootstrapLoading: true, bootstrapError: null });
      store.setLoading(true);
      try {
        const payload = await fetchCalendarBootstrap({
          timeMin: options?.timeMin,
          timeMax: options?.timeMax,
        });
        store.hydrate(payload);
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to load calendar data';
        set({ bootstrapError: message });
        store.setError(message);
      } finally {
        set({ bootstrapLoading: false });
        store.setLoading(false);
      }
    },
    fetchEventsForRange: async (options) => {
      const store = useCalendarStore.getState();
      const calendarUids = options?.calendarUids && options.calendarUids.length
        ? options.calendarUids
        : resolveCalendarsForFetch(store);
      if (!calendarUids.length) {
        return;
      }
      const defaultRange = computeDefaultRange();
      const range = {
        timeMin: options?.timeMin ?? store.range?.timeMin ?? defaultRange.timeMin,
        timeMax: options?.timeMax ?? store.range?.timeMax ?? defaultRange.timeMax,
      };
      const extraKey = `${options?.query ?? ''}|${options?.showDeleted ? '1' : '0'}`;
      const rangeKey = buildRangeKey(calendarUids, range.timeMin, range.timeMax, extraKey);
      if (get().rangeLoadingKey === rangeKey) {
        return;
      }
      set({ rangeLoadingKey: rangeKey });
      store.setLoading(true);
      try {
        const response = await fetchCalendarEvents({
          calendarUids,
          timeMin: range.timeMin,
          timeMax: range.timeMax,
          showDeleted: options?.showDeleted,
          query: options?.query ?? null,
          singleEvents: true,
          timeZone: store.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        const replace = options?.replace ?? false;
        store.upsertEvents(response.events, { replace });
        const previousRange = store.range;
        const nextRange = replace ? response.range : mergeRanges(previousRange, response.range);
        store.setRange(nextRange);
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to load events';
        store.setError(message);
      } finally {
        set({ rangeLoadingKey: null });
        store.setLoading(false);
      }
    },
    refreshCalendars: async (options) => {
      const store = useCalendarStore.getState();
      set({ refreshingCalendars: true });
      try {
        const calendars = await fetchCalendars({
          includeHidden: true,
          accountId: options?.accountId ?? null,
        });
        store.upsertCalendars(calendars);
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to refresh calendars';
        store.setError(message);
      } finally {
        set({ refreshingCalendars: false });
      }
    },
    prefetchRangeAround: async (focusDate, options) => {
      if (!focusDate) {
        return;
      }
      const anchor = parseDateOnly(focusDate);
      if (Number.isNaN(anchor.getTime())) {
        return;
      }
      const before = options?.daysBefore ?? 21;
      const after = options?.daysAfter ?? 60;
      const windowStart = addDays(anchor, -before).toISOString();
      const windowEnd = addDays(anchor, after).toISOString();
      await get().fetchEventsForRange({ timeMin: windowStart, timeMax: windowEnd, replace: false });
    },
    createEvent: async (input) => {
      const store = useCalendarStore.getState();
      set({ creatingEvent: true, mutationError: null });
      try {
        const event = await createCalendarEvent(input);
        store.upsertEvents([event]);
        const eventRange = deriveEventRange(event);
        if (eventRange) {
          store.setRange(mergeRanges(store.range, eventRange));
        }
        return event;
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to create event';
        set({ mutationError: message });
        store.setError(message);
        return null;
      } finally {
        set({ creatingEvent: false });
      }
    },
    updateEvent: async (input) => {
      const store = useCalendarStore.getState();
      set({ updatingEventId: input.eventId, mutationError: null });
      try {
        const event = await updateCalendarEvent(input);
        store.upsertEvents([event]);
        const eventRange = deriveEventRange(event);
        if (eventRange) {
          store.setRange(mergeRanges(store.range, eventRange));
        }
        return event;
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to update event';
        set({ mutationError: message });
        store.setError(message);
        return null;
      } finally {
        set({ updatingEventId: null });
      }
    },
    deleteEvent: async (input) => {
      const store = useCalendarStore.getState();
      set({ deletingEventId: input.eventId, mutationError: null });
      try {
        await deleteCalendarEvent(input);
        store.removeEvent(input.eventId);
        return true;
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to delete event';
        set({ mutationError: message });
        store.setError(message);
        return false;
      } finally {
        set({ deletingEventId: null });
      }
    },
    updateCalendarColor: async (input) => {
      const store = useCalendarStore.getState();
      try {
        const calendar = await updateCalendarColorApi(input);
        store.upsertCalendars([calendar]);
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to update calendar color';
        store.setError(message);
      }
    },
    searchEvents: async (options) => {
      const store = useCalendarStore.getState();
      const calendarUids = options?.calendarUids && options.calendarUids.length
        ? options.calendarUids
        : store.useDefaultVisibleCalendars
          ? store.defaultCalendarUids
          : store.visibleCalendarUids;
      if (!calendarUids.length) {
        set({ searchResults: [], searchRange: null, searchError: null });
        return [];
      }
      set({ searchLoading: true, searchError: null });
      try {
        const response = await searchCalendarEvents({
          ...options,
          calendarUids,
          // Don't send timeMin/timeMax to use backend's wide default search range (10 years past/future)
          timeMin: options?.timeMin,
          timeMax: options?.timeMax,
        });
        store.upsertEvents(response.events, { replace: false });
        set({
          searchLoading: false,
          searchError: null,
          searchResults: response.events.map((event) => event.id),
          searchRange: response.range,
          lastSearchContext: {
            query: options?.query ?? null,
            calendarUids,
          },
        });
        return response.events;
      } catch (error) {
        const message = getErrorMessage(error) ?? 'Failed to search events';
        set({ searchLoading: false, searchError: message, searchResults: [] });
        useCalendarStore.getState().setError(message);
        return [];
      }
    },
    clearSearchResults: () => set({ searchResults: [], searchRange: null, lastSearchContext: null, searchError: null }),
  }), { name: 'CalendarActionsStore' })
);
